/**
 * Product and service tax rates for a Quebec-based company selling to Canadian residents.
 *
 * Place-of-supply rules: Tax is determined by the CUSTOMER's province (destination),
 * not the business location. We collect only the tax required by the customer's province:
 * - Quebec customers: GST + QST
 * - HST provinces (ON, NB, NL, NS, PE): HST
 * - BC, SK, MB (PST provinces): GST only (5%) - no nexus, so no PST collected
 * - GST-only (AB, NT, NU, YT): GST only
 * - Non-Canadian residents: no Canadian tax
 *
 * PST nexus: BC, SK, MB require PST only if we have nexus (physical presence,
 * employees, or meet revenue thresholds for digital services). Without nexus,
 * we charge GST only (5%) for those provinces.
 *
 * Rates as of 2025. Nova Scotia HST reduced to 14% effective April 1, 2025.
 * Province names must match states.ts for lookup (case-insensitive).
 */
export type CanadianTaxType = 'GST' | 'HST' | 'GST+PST' | 'GST+QST';

export interface TaxRate {
  totalRate: number;
  gstRate: number;
  provincialRate?: number;
  taxType: CanadianTaxType;
}

export interface TaxResult {
  totalRate: number;
  gstRate: number;
  provincialRate: number;
  taxType: CanadianTaxType;
  province: string;
}

/** Canadian province/territory tax rates. Keys match state names in states.ts */
export const canadianProvinceTaxRates: { [provinceName: string]: TaxRate } = {
  Alberta: { totalRate: 0.05, gstRate: 0.05, taxType: 'GST' },
  'British Columbia': {
    totalRate: 0.05,
    gstRate: 0.05,
    taxType: 'GST',
  },
  Manitoba: {
    totalRate: 0.05,
    gstRate: 0.05,
    taxType: 'GST',
  },
  'New Brunswick': {
    totalRate: 0.15,
    gstRate: 0.05,
    provincialRate: 0.1,
    taxType: 'HST',
  },
  'Newfoundland and Labrador': {
    totalRate: 0.15,
    gstRate: 0.05,
    provincialRate: 0.1,
    taxType: 'HST',
  },
  'Northwest Territories': {
    totalRate: 0.05,
    gstRate: 0.05,
    taxType: 'GST',
  },
  'Nova Scotia': {
    totalRate: 0.14,
    gstRate: 0.05,
    provincialRate: 0.09,
    taxType: 'HST',
  },
  Nunavut: { totalRate: 0.05, gstRate: 0.05, taxType: 'GST' },
  Ontario: {
    totalRate: 0.13,
    gstRate: 0.05,
    provincialRate: 0.08,
    taxType: 'HST',
  },
  'Prince Edward Island': {
    totalRate: 0.15,
    gstRate: 0.05,
    provincialRate: 0.1,
    taxType: 'HST',
  },
  Quebec: {
    totalRate: 0.14975,
    gstRate: 0.05,
    provincialRate: 0.09975,
    taxType: 'GST+QST',
  },
  Saskatchewan: {
    totalRate: 0.05,
    gstRate: 0.05,
    taxType: 'GST',
  },
  'Yukon Territory': { totalRate: 0.05, gstRate: 0.05, taxType: 'GST' },
};

/** Country code for Canada */
export const CANADA_COUNTRY_CODE = 'CA';

/** Business is Quebec-based; tax collected is based on customer's province only */
export const BUSINESS_PROVINCE = 'Quebec';

/**
 * Find province key in canadianProvinceTaxRates (case-insensitive).
 */
function findProvinceKey(input: string): string | null {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;
  const key = Object.keys(canadianProvinceTaxRates).find(
    (k) => k.toLowerCase() === normalized
  );
  return key ?? null;
}

/**
 * Get the applicable tax rate for a Canadian resident based on their province.
 * For a Quebec-based company: we collect only the tax required by the customer's
 * province (place-of-supply rules). Returns null for non-Canadian residents.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g. 'CA', 'US')
 * @param stateProvince - Province/territory name (must match states.ts for Canada)
 * @returns TaxResult for Canadian residents, null for others
 */
export function getTaxRateForLocation(
  countryCode: string,
  stateProvince?: string
): TaxResult | null {
  if (countryCode !== CANADA_COUNTRY_CODE) {
    return null;
  }

  const provinceKey = findProvinceKey(stateProvince ?? '');
  if (!provinceKey) {
    return null;
  }

  const taxRate = canadianProvinceTaxRates[provinceKey];
  if (!taxRate) {
    return null;
  }

  return {
    totalRate: taxRate.totalRate,
    gstRate: taxRate.gstRate,
    provincialRate: taxRate.provincialRate ?? 0,
    taxType: taxRate.taxType,
    province: provinceKey,
  };
}

/**
 * Get the total tax rate as a decimal (e.g. 0.13 for 13%).
 * Returns 0 for non-Canadian or unknown province.
 */
export function getTotalTaxRate(
  countryCode: string,
  stateProvince?: string
): number {
  const result = getTaxRateForLocation(countryCode, stateProvince);
  return result?.totalRate ?? 0;
}

/** Single tax line (GST, QST, HST, PST, etc.) with rate and amount */
export interface TaxLineItem {
  /** i18n key for the tax name (e.g. 'GST', 'QST', 'HST', 'PST', 'provincial') */
  labelKey: string;
  rate: number;
  amount: number;
}

export interface PriceWithTax {
  displayPrice: number;
  taxRate: number;
  taxLabel: string | null;
  /** Breakdown by tax type (GST, QST, HST, PST, etc.) - empty when no tax */
  taxBreakdown: TaxLineItem[];
}

/**
 * Build tax breakdown from TaxResult. Returns empty array when no tax.
 * For HST provinces: single HST line (no GST/Provincial split).
 * For GST+QST, GST+PST: separate GST and provincial lines.
 * For GST-only: single GST line.
 */
function buildTaxBreakdown(
  basePrice: number,
  taxResult: TaxResult
): TaxLineItem[] {
  if (taxResult.totalRate === 0) {
    return [];
  }
  if (taxResult.taxType === 'HST') {
    return [
      {
        labelKey: 'HST',
        rate: taxResult.totalRate,
        amount: Math.round(basePrice * taxResult.totalRate * 100) / 100,
      },
    ];
  }
  const items: TaxLineItem[] = [];
  if (taxResult.gstRate > 0) {
    items.push({
      labelKey: 'GST',
      rate: taxResult.gstRate,
      amount: Math.round(basePrice * taxResult.gstRate * 100) / 100,
    });
  }
  if (taxResult.provincialRate > 0) {
    const provincialLabel =
      taxResult.taxType === 'GST+QST'
        ? 'QST'
        : taxResult.taxType === 'GST+PST'
          ? 'PST'
          : 'provincial';
    items.push({
      labelKey: provincialLabel,
      rate: taxResult.provincialRate,
      amount: Math.round(basePrice * taxResult.provincialRate * 100) / 100,
    });
  }
  return items;
}

/**
 * Get price including tax for display. Tax is based on the customer's location
 * (Canadian residents pay applicable provincial/federal tax), not the display currency.
 * The basePrice is in the selected currency; tax is applied and amounts are in that same currency.
 */
export function getPriceWithTax(
  basePrice: number,
  countryCode?: string,
  stateProvince?: string,
  _currency?: string
): PriceWithTax {
  const taxResult = getTaxRateForLocation(countryCode || '', stateProvince);
  if (!taxResult || taxResult.totalRate === 0) {
    return { displayPrice: basePrice, taxRate: 0, taxLabel: null, taxBreakdown: [] };
  }
  const displayPrice = Math.round(basePrice * (1 + taxResult.totalRate) * 100) / 100;
  const taxLabel = `${(taxResult.totalRate * 100).toFixed(2)}%`;
  const taxBreakdown = buildTaxBreakdown(basePrice, taxResult);
  return {
    displayPrice,
    taxRate: taxResult.totalRate,
    taxLabel,
    taxBreakdown,
  };
}
